import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export type UsageData = {
  totalDurationMs: number;
  totalDurationMinutes: number;
  totalSessions: number;
  agentBreakdown: {
    agentId: string;
    agentName: string;
    durationMs: number;
    durationMinutes: number;
    sessionCount: number;
  }[];
};

export type UsagePeriodData = {
  id?: string; // For Redux slice compatibility
  startDate: string;
  endDate: string;
  organizationId: string;
  usage: UsageData;
};

export type UsageReportParams = {
  startDate: string;
  endDate: string;
  organizationId?: string;
};

export type MonthlyUsageParams = {
  year: string;
  month: string;
  organizationId?: string;
};

export type UsageState = {
  currentReport: UsagePeriodData | null;
  isLoading: boolean;
  error: string | null;
};

// Async thunks
export const getUsageReport = createAsyncThunk("usage/getUsageReport", async (params: UsageReportParams) => {
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

  const { data } = await makeRequest<{ data: UsagePeriodData }>(`/v1/usage/report?${queryString}`);
  return data;
});

export const getMonthlyUsage = createAsyncThunk("usage/getMonthlyUsage", async (params: MonthlyUsageParams) => {
  const queryString = Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");

  const { data } = await makeRequest<{ data: UsagePeriodData }>(`/v1/usage/monthly?${queryString}`);
  return data;
});

const initialState: UsageState = {
  currentReport: null,
  isLoading: false,
  error: null,
};

const usageSlice = createSlice({
  name: "usage",
  initialState,
  reducers: {
    clearUsageData: (state) => {
      state.currentReport = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // getUsageReport cases
    builder.addCase(getUsageReport.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getUsageReport.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentReport = action.payload;
      state.error = null;
    });
    builder.addCase(getUsageReport.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch usage report";
    });

    // getMonthlyUsage cases
    builder.addCase(getMonthlyUsage.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(getMonthlyUsage.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentReport = action.payload;
      state.error = null;
    });
    builder.addCase(getMonthlyUsage.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch monthly usage";
    });
  },
});

export const { clearUsageData } = usageSlice.actions;
export const usageReducer = usageSlice.reducer;
